import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  FileText, DollarSign, Search, AlertTriangle,
  CheckCircle, Clock, Loader2, RefreshCw,
  TrendingUp, History, Building2, Users,
  ChevronLeft, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 50;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    minimumFractionDigits: 2
  }).format(amount || 0).replace('GTQ', 'Q');
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return format(date, 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
};

const AccountsReceivable = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ medicos: [], instituciones: [] });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [medicoFilter, setMedicoFilter] = useState('all');
  const [institucionFilter, setInstitucionFilter] = useState('all');
  const [showHistorico, setShowHistorico] = useState(true);
  
  // Tab for IGSS vs Privados
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'igss', 'privados'
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  // Payment modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ monto: '', metodo_pago: 'Efectivo', notas: '' });
  const [savingPayment, setSavingPayment] = useState(false);
  
  // History modal
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedAccountHistory, setSelectedAccountHistory] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [accountsRes, summaryRes, filtersRes] = await Promise.all([
        axios.get(`${API_URL}/api/accounts-receivable/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/accounts-receivable/summary-all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/accounts-receivable/filters`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAccounts(accountsRes.data || []);
      setSummary(summaryRes.data);
      setFilters(filtersRes.data || { medicos: [], instituciones: [] });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar las cuentas por cobrar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, medicoFilter, institucionFilter, showHistorico, activeTab]);

  // Helper to check if account is IGSS
  const isIGSS = (account) => {
    return account.institucion?.toUpperCase() === 'IGSS' || 
           account.igss_afiliacion || 
           account.igss_codigo;
  };

  // Filter accounts
  const filteredAccounts = useMemo(() => {
    return accounts.filter(account => {
      // Tab filter (IGSS vs Privados)
      if (activeTab === 'igss' && !isIGSS(account)) return false;
      if (activeTab === 'privados' && isIGSS(account)) return false;
      
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = 
          account.nombre?.toLowerCase().includes(search) ||
          account.telefono?.includes(search) ||
          account.medico_referente?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'pendiente' && account.estado_cuenta !== 'pendiente') return false;
        if (statusFilter === 'parcial' && account.estado_cuenta !== 'parcial') return false;
        if (statusFilter === 'pagado' && account.estado_cuenta !== 'pagado') return false;
        if (statusFilter === 'vencido' && !account.vencido) return false;
      }
      
      // Hide paid accounts unless showing historic
      if (!showHistorico && account.estado_cuenta === 'pagado') return false;
      
      // Medico filter
      if (medicoFilter !== 'all' && account.medico_referente !== medicoFilter) return false;
      
      // Institution filter
      if (institucionFilter !== 'all' && account.institucion !== institucionFilter) return false;
      
      return true;
    });
  }, [accounts, searchTerm, statusFilter, medicoFilter, institucionFilter, showHistorico, activeTab]);

  // Calculate summary for current tab
  const tabSummary = useMemo(() => {
    const tabAccounts = accounts.filter(account => {
      if (activeTab === 'igss') return isIGSS(account);
      if (activeTab === 'privados') return !isIGSS(account);
      return true;
    });
    
    let total_cuentas = tabAccounts.length;
    let total_monto = 0;
    let total_cobrado = 0;
    let total_pendiente = 0;
    let cuentas_pendientes = 0;
    let cuentas_parciales = 0;
    let cuentas_pagadas = 0;
    let cuentas_vencidas = 0;
    
    tabAccounts.forEach(acc => {
      total_monto += acc.total || 0;
      const abonos = acc.abonos?.reduce((sum, a) => sum + (a.monto || 0), 0) || 0;
      total_cobrado += abonos;
      
      if (acc.estado_cuenta === 'pagado') {
        cuentas_pagadas++;
      } else {
        total_pendiente += acc.monto_pendiente || 0;
        if (acc.estado_cuenta === 'parcial') cuentas_parciales++;
        else cuentas_pendientes++;
        if (acc.vencido) cuentas_vencidas++;
      }
    });
    
    return {
      total_cuentas,
      total_monto,
      total_cobrado,
      total_pendiente,
      cuentas_pendientes,
      cuentas_parciales,
      cuentas_pagadas,
      cuentas_vencidas
    };
  }, [accounts, activeTab]);

  // Count by type for tabs
  const countByType = useMemo(() => {
    let igss = 0;
    let privados = 0;
    accounts.forEach(acc => {
      if (isIGSS(acc)) igss++;
      else privados++;
    });
    return { igss, privados, all: accounts.length };
  }, [accounts]);

  // Pagination
  const totalPages = Math.ceil(filteredAccounts.length / ITEMS_PER_PAGE);
  const paginatedAccounts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAccounts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAccounts, currentPage]);

  const openPaymentModal = (account) => {
    setSelectedAccount(account);
    setPaymentForm({ monto: '', metodo_pago: 'Efectivo', notas: '' });
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setSelectedAccount(null);
    setPaymentForm({ monto: '', metodo_pago: 'Efectivo', notas: '' });
  };

  const handleRegisterPayment = async () => {
    if (!selectedAccount || !paymentForm.monto) {
      toast.error('Ingrese el monto del pago');
      return;
    }

    const monto = parseFloat(paymentForm.monto);
    if (isNaN(monto) || monto <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }

    if (monto > selectedAccount.monto_pendiente) {
      toast.error(`El monto no puede exceder el pendiente (${formatCurrency(selectedAccount.monto_pendiente)})`);
      return;
    }

    setSavingPayment(true);
    try {
      await axios.post(
        `${API_URL}/api/accounts-receivable/${selectedAccount.id}/abono`,
        null,
        {
          params: {
            monto: monto,
            metodo_pago: paymentForm.metodo_pago,
            notas: paymentForm.notas
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Pago registrado exitosamente');
      closePaymentModal();
      fetchData(); // Refresh
    } catch (error) {
      console.error('Error registering payment:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar el pago');
    } finally {
      setSavingPayment(false);
    }
  };

  const openHistoryModal = (account) => {
    setSelectedAccountHistory(account);
    setHistoryModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounts-receivable-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-7 h-7 text-orange-600" />
            Cuentas por Cobrar
          </h1>
          <p className="text-slate-500 mt-1">Gestiona todas las cuentas pendientes de pago</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

      {/* Tabs IGSS / Privados */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'all' 
              ? 'border-blue-600 text-blue-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <FileText className="w-4 h-4" />
          Todas ({countByType.all})
        </button>
        <button
          onClick={() => setActiveTab('igss')}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'igss' 
              ? 'border-emerald-600 text-emerald-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Building2 className="w-4 h-4" />
          IGSS ({countByType.igss})
        </button>
        <button
          onClick={() => setActiveTab('privados')}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'privados' 
              ? 'border-purple-600 text-purple-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Users className="w-4 h-4" />
          Privados ({countByType.privados})
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{tabSummary.total_cuentas}</p>
            <p className="text-sm text-blue-600">Total Cuentas</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-700">{formatCurrency(tabSummary.total_monto)}</p>
            <p className="text-sm text-slate-600">Total Facturado</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(tabSummary.total_cobrado)}</p>
            <p className="text-sm text-emerald-600">Total Cobrado</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-700">{formatCurrency(tabSummary.total_pendiente)}</p>
            <p className="text-sm text-red-600">Total Pendiente</p>
          </CardContent>
        </Card>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-amber-200">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-xl font-bold text-amber-700">{tabSummary.cuentas_pendientes}</p>
              <p className="text-sm text-slate-600">Pendientes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="p-4 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-xl font-bold text-blue-700">{tabSummary.cuentas_parciales}</p>
              <p className="text-sm text-slate-600">Parciales</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
            <div>
              <p className="text-xl font-bold text-emerald-700">{tabSummary.cuentas_pagadas}</p>
              <p className="text-sm text-slate-600">Pagadas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-xl font-bold text-red-700">{tabSummary.cuentas_vencidas}</p>
              <p className="text-sm text-slate-600">Vencidas</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre, teléfono o médico..."
                className="pl-10"
                data-testid="search-accounts"
              />
            </div>
            
            {/* Status filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pendiente">Pendientes</SelectItem>
                <SelectItem value="parcial">Parciales</SelectItem>
                <SelectItem value="pagado">Pagadas</SelectItem>
                <SelectItem value="vencido">Vencidas</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Medico filter */}
            <Select value={medicoFilter} onValueChange={setMedicoFilter}>
              <SelectTrigger className="w-[160px]" data-testid="medico-filter">
                <SelectValue placeholder="Médico" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {filters.medicos.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Show historic toggle */}
            <Button
              variant={showHistorico ? "default" : "outline"}
              onClick={() => setShowHistorico(!showHistorico)}
              className="gap-2"
            >
              <History className="w-4 h-4" />
              {showHistorico ? 'Mostrando todas' : 'Ocultando pagadas'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Accounts Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              {activeTab === 'igss' ? 'Cuentas IGSS' : activeTab === 'privados' ? 'Cuentas Privados' : 'Todas las Cuentas'} ({filteredAccounts.length})
            </CardTitle>
            {/* Pagination info */}
            {totalPages > 1 && (
              <p className="text-sm text-slate-500">
                Página {currentPage} de {totalPages} ({filteredAccounts.length} registros)
              </p>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-slate-700">
                  <th className="p-3 text-left font-semibold">Fecha</th>
                  <th className="p-3 text-left font-semibold">Paciente</th>
                  <th className="p-3 text-left font-semibold">Teléfono</th>
                  <th className="p-3 text-left font-semibold">Médico Ref.</th>
                  <th className="p-3 text-left font-semibold">Institución</th>
                  <th className="p-3 text-left font-semibold">Examen</th>
                  <th className="p-3 text-right font-semibold">Total</th>
                  <th className="p-3 text-right font-semibold">Abonado</th>
                  <th className="p-3 text-right font-semibold">Pendiente</th>
                  <th className="p-3 text-center font-semibold">F. Límite</th>
                  <th className="p-3 text-center font-semibold">Estado</th>
                  <th className="p-3 text-center font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAccounts.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="p-8 text-center text-slate-400">
                      No hay cuentas por cobrar que coincidan con los filtros
                    </td>
                  </tr>
                ) : (
                  paginatedAccounts.map((item, index) => {
                    const tipoExamen = item.examenes?.length > 0 
                      ? item.examenes.map(e => e.tipo_examen).join(', ')
                      : item.tipo_examen || '-';
                    const totalAbonos = item.abonos?.reduce((sum, a) => sum + (a.monto || 0), 0) || 0;
                    const itemIsIGSS = isIGSS(item);
                    
                    return (
                      <tr 
                        key={item.id || index} 
                        className={`border-b border-slate-100 hover:bg-slate-50 ${item.vencido ? 'bg-red-50' : ''}`}
                      >
                        <td className="p-3 font-mono text-slate-600">{formatDateDisplay(item.fecha)}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{item.nombre}</span>
                            {itemIsIGSS && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0">IGSS</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-slate-600">{item.telefono}</td>
                        <td className="p-3 text-slate-600">{item.medico_referente || '-'}</td>
                        <td className="p-3 text-slate-600">{item.institucion || '-'}</td>
                        <td className="p-3 text-slate-600 max-w-[150px] truncate" title={tipoExamen}>{tipoExamen}</td>
                        <td className="p-3 text-right font-semibold">{formatCurrency(item.total)}</td>
                        <td className="p-3 text-right text-emerald-600">{formatCurrency(totalAbonos)}</td>
                        <td className="p-3 text-right font-semibold text-red-600">{formatCurrency(item.monto_pendiente)}</td>
                        <td className="p-3 text-center font-mono text-sm">
                          {item.fecha_limite_pago ? formatDateDisplay(item.fecha_limite_pago) : '-'}
                        </td>
                        <td className="p-3 text-center">
                          {item.estado_cuenta === 'pagado' ? (
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Pagado</Badge>
                          ) : item.vencido ? (
                            <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Vencido</Badge>
                          ) : item.estado_cuenta === 'parcial' ? (
                            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Parcial</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pendiente</Badge>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {item.abonos && item.abonos.length > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                onClick={() => openHistoryModal(item)}
                                title="Ver historial de pagos"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                            )}
                            {item.estado_cuenta !== 'pagado' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                onClick={() => openPaymentModal(item)}
                                data-testid={`pay-button-${item.id}`}
                              >
                                <DollarSign className="w-4 h-4 mr-1" />
                                Pagar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-slate-500">
                Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredAccounts.length)} de {filteredAccounts.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        className="w-8 h-8 p-0"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Modal */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <DollarSign className="w-5 h-5" />
              Registrar Pago
            </DialogTitle>
          </DialogHeader>
          
          {selectedAccount && (
            <div className="space-y-4">
              {/* Account Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Paciente:</span>
                  <span className="font-medium">{selectedAccount.nombre}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Fecha:</span>
                  <span className="font-mono text-sm">{formatDateDisplay(selectedAccount.fecha)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Total:</span>
                  <span className="font-semibold">{formatCurrency(selectedAccount.total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-600">Abonado:</span>
                  <span className="text-emerald-600">
                    {formatCurrency(selectedAccount.abonos?.reduce((sum, a) => sum + (a.monto || 0), 0) || 0)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="text-sm font-medium text-slate-700">Pendiente:</span>
                  <span className="font-bold text-red-600">{formatCurrency(selectedAccount.monto_pendiente)}</span>
                </div>
              </div>

              {/* Payment Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="payment_amount">Monto a Pagar *</Label>
                  <Input
                    id="payment_amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedAccount.monto_pendiente}
                    value={paymentForm.monto}
                    onChange={(e) => setPaymentForm({ ...paymentForm, monto: e.target.value })}
                    placeholder="0.00"
                    className="bg-white"
                    data-testid="payment-amount"
                  />
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="text-xs p-0 h-auto text-blue-600"
                    onClick={() => setPaymentForm({ ...paymentForm, monto: selectedAccount.monto_pendiente.toString() })}
                  >
                    Pagar total pendiente
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Método de Pago</Label>
                  <Select
                    value={paymentForm.metodo_pago}
                    onValueChange={(value) => setPaymentForm({ ...paymentForm, metodo_pago: value })}
                  >
                    <SelectTrigger className="bg-white" data-testid="payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Efectivo">Efectivo</SelectItem>
                      <SelectItem value="Tarjeta Crédito/Débito">Tarjeta Crédito/Débito</SelectItem>
                      <SelectItem value="Transferencia">Transferencia</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payment_notes">Notas</Label>
                  <Textarea
                    id="payment_notes"
                    value={paymentForm.notas}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notas: e.target.value })}
                    placeholder="Observaciones del pago..."
                    className="bg-white resize-none"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closePaymentModal}>
              Cancelar
            </Button>
            <Button 
              onClick={handleRegisterPayment} 
              disabled={savingPayment}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-payment"
            >
              {savingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Registrando...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Registrar Pago
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <History className="w-5 h-5" />
              Historial de Pagos
            </DialogTitle>
          </DialogHeader>
          
          {selectedAccountHistory && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium text-slate-900">{selectedAccountHistory.nombre}</p>
                <p className="text-sm text-slate-600">
                  Total: {formatCurrency(selectedAccountHistory.total)} | 
                  Pendiente: {formatCurrency(selectedAccountHistory.monto_pendiente)}
                </p>
              </div>
              
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {(selectedAccountHistory.abonos || []).map((abono, idx) => (
                  <div key={idx} className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-emerald-700">{formatCurrency(abono.monto)}</p>
                        <p className="text-xs text-slate-600">{abono.metodo_pago}</p>
                        {abono.notas && <p className="text-xs text-slate-500 mt-1">{abono.notas}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono text-slate-600">{formatDateDisplay(abono.fecha)}</p>
                        <p className="text-xs text-slate-500">{abono.registrado_por}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryModalOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsReceivable;
